export const SpreadsheetTemplateType = {
    EaWasteReturn: "EaWasteReturn",
} as const;

export const WasteReturnFixedCellKeys = {
    SiteOperatorName: "siteOperatorName",
    PermitNumber: "permitNumber",
    SiteName: "siteName",
    ReportingPeriod: "reportingPeriod",
    ReportingYear: "reportingYear",
    FacilityOperation: "facilityOperation",
    SiteAddress: "siteAddress",
    SitePostcode: "sitePostcode",
    WeighbridgeUsed: "weighbridgeUsed",
    PercentageWeighed: "percentageWeighed",
    FormComplete: "formComplete",
    SignatoryName: "signatoryName",
    SignatoryPosition: "signatoryPosition",
    SignatoryPhone: "signatoryPhone",
    NilReturn: "nilReturn",
    FormCompletionDate: "formCompletionDate",
    CompetenceScheme: "competenceScheme",
} as const;

export const WasteReturnTableKeys = {
    WasteReceived: "wasteReceived",
    WasteRemoved: "wasteRemoved",
} as const;

export const WasteReturnTableColumnKeys = {
    Origin: "origin",
    Destination: "destination",
    EwcCode: "ewcCode",
    DisposalRecoveryCode: "disposalRecoveryCode",
    MunicipalFlag: "municipalFlag",
    DegradableFlag: "degradableFlag",
    PhysicalState: "physicalState",
    SourceActivity: "sourceActivity",
    AmountTonnes: "amountTonnes",
    PreTreatment: "preTreatment",
} as const;

export const WasteReturnRowModes = {
    FixedRange: "fixedRange",
} as const;

export const WasteReturnOverflowStrategies = {
    Fail: "fail",
} as const;

export const WasteReturnValueFormatters = {
    Code: "code",
    Description: "description",
    CodeDescription: "codeDescription",
    YesNo: "yesNo",
    Raw: "raw",
    Decimal: "decimal",
    MappedOverride: "mappedOverride",
    Date: "date",
} as const;

export const WasteReturnRegFieldKeys = {
    Ewc: "ewc",
    EaDrReceived: "ea_dr_received",
    EaDrRemoved: "ea_dr_removed",
    EaDr: "ea_dr",
    PhysicalState: "physical_state",
    Municipal: "municipal",
    Degradable: "degradable",
    SourceActivity: "source_activity",
    PreTreatment: "pre_treatment",
    Geography: "geography",
    FacilityOperation: "facility_operation",
    TechnicalCompetence: "technical_competence",
} as const;

export const WasteReturnListSourceTypes = {
    LiteralList: "literalList",
    DirectRange: "directRange",
    NamedRange: "namedRange",
    DynamicFormula: "dynamicFormula",
    Unsupported: "unsupported",
} as const;

export const WasteReturnPairedColumnInputKeys = {
    TcManagers: "tcManagers",
} as const;

export const WasteReturnPairedColumnRoles = {
    Names: "names",
    Dobs: "dobs",
} as const;

export type WasteReturnFixedCellKey = (typeof WasteReturnFixedCellKeys)[keyof typeof WasteReturnFixedCellKeys];
export type WasteReturnRegFieldKey = (typeof WasteReturnRegFieldKeys)[keyof typeof WasteReturnRegFieldKeys];

export interface ISpreadsheetTemplateActive {
    id: string;
    templateType: string;
    name?: string | null;
    originalFileName: string;
    contentType: string;
    fileSizeBytes: number;
    fileHashSha256: string;
    createdAtUtc: string;
    updatedAtUtc: string;
    createdByIdentityId: string;
    updatedByIdentityId: string;
    hasMapping: boolean;
    validationSummary?: IMappingValidationResult | null;
}

export interface ISpreadsheetTemplateUploadValidationSummary {
    worksheetCount: number;
    validatedAtUtc: string;
    fileHashSha256: string;
}

export interface ISpreadsheetTemplateUploadResult extends ISpreadsheetTemplateActive {
    worksheetCount: number;
    uploadValidationSummary?: ISpreadsheetTemplateUploadValidationSummary | null;
}

export interface IWasteReturnFixedCellMapping {
    sheet: string;
    address: string;
    valueFormatter: string;
    regFieldKey?: string | null;
    maxLength?: number | null;
}

export interface IWasteReturnTableColumnMapping {
    column: string;
    regFieldKey?: string | null;
    valueFormatter: string;
}

export interface IWasteReturnTableMapping {
    sheet: string;
    firstDataRow: number;
    rowMode: string;
    maxRows: number;
    overflowStrategy: string;
    columns: Record<string, IWasteReturnTableColumnMapping>;
}

export interface IWasteReturnTableMappings {
    wasteReceived?: IWasteReturnTableMapping | null;
    wasteRemoved?: IWasteReturnTableMapping | null;
}

export interface IWasteReturnListSourceDefinition {
    sourceType: string;
    sheet: string;
    valueRange?: string | null;
    primaryValueField: string;
    adjacentColumnMap?: Record<string, string>;
}

export interface IExtractedTemplateValue {
    rawValue: string;
    normalizedKey: string;
    parsed: Record<string, unknown>;
    sourceSheet?: string | null;
    sourceRange?: string | null;
    sourceRow?: number | null;
    adjacentValues?: Record<string, string | null>;
}

export interface IWasteReturnListSourceMapping {
    autoDetected?: IWasteReturnListSourceDefinition | null;
    manualOverride?: IWasteReturnListSourceDefinition | null;
    extractedValues?: IExtractedTemplateValue[];
}

export interface IWasteReturnPairedColumnRangeMapping {
    sheet: string;
    address: string;
}

export interface IWasteReturnPairedColumnInputMapping {
    names: IWasteReturnPairedColumnRangeMapping;
    dobs: IWasteReturnPairedColumnRangeMapping;
}

export interface IWasteReturnTemplateMapping {
    schemaVersion: number;
    templateType: string;
    fixedCells: Record<string, IWasteReturnFixedCellMapping>;
    tables: IWasteReturnTableMappings;
    listSources: Record<string, IWasteReturnListSourceMapping>;
    pairedColumnInputs?: Record<string, IWasteReturnPairedColumnInputMapping>;
}

export interface IWorkbookInspectionSummary {
    worksheetCount: number;
    namedRangeCount: number;
    dataValidationCount: number;
    warningCount: number;
}

export interface IWorkbookInspectionWorksheet {
    name: string;
    index: number;
    visibility: string;
    isProtected: boolean;
    usedRange?: string | null;
    rowCount?: number | null;
    columnCount?: number | null;
    mergedCellCount: number;
    formulaCellCount: number;
    dataValidationCount: number;
}

export interface IWorkbookInspectionNamedRange {
    name: string;
    refersTo?: string | null;
    worksheetScope?: string | null;
    isBroken: boolean;
    isHidden: boolean;
}

export interface IWorkbookInspectionDataValidation {
    worksheet: string;
    address: string;
    validationType: string;
    formulaOrSource?: string | null;
    sourceClassification: string;
}

export interface IWorkbookInspectionWarning {
    code: string;
    message: string;
    worksheet?: string | null;
    detail?: string | null;
}

export interface IWorkbookInspection {
    schemaVersion: number;
    templateId: string;
    fileHashSha256: string;
    inspectedAtUtc: string;
    workbook: IWorkbookInspectionSummary;
    worksheets: IWorkbookInspectionWorksheet[];
    namedRanges: IWorkbookInspectionNamedRange[];
    dataValidations: IWorkbookInspectionDataValidation[];
    warnings: IWorkbookInspectionWarning[];
}

export interface IWorkbookInspectionResponse {
    template: ISpreadsheetTemplateActive;
    inspection: IWorkbookInspection;
    warnings: IWorkbookInspectionWarning[];
    fromCache: boolean;
}

export interface IMappingValidationIssue {
    code: string;
    message: string;
    section: string;
    fieldKey?: string | null;
    sheet?: string | null;
    address?: string | null;
    table?: string | null;
    column?: string | null;
}

export interface IMappingValidationResult {
    isValid: boolean;
    isExportReady: boolean;
    errors: IMappingValidationIssue[];
    warnings: IMappingValidationIssue[];
    checkedAtUtc: string;
}

export interface ISpreadsheetTemplateSaveMappingResult {
    template: ISpreadsheetTemplateActive;
    mapping: IWasteReturnTemplateMapping;
    validation: IMappingValidationResult;
}

export interface ISpreadsheetTemplateGetMappingResult {
    template: ISpreadsheetTemplateActive;
    mapping: IWasteReturnTemplateMapping | null;
    validation: IMappingValidationResult | null;
    hasMapping: boolean;
    hasValidation: boolean;
}

export interface ISpreadsheetTemplateValidateMappingResult {
    template?: ISpreadsheetTemplateActive | null;
    mapping: IWasteReturnTemplateMapping;
    validation: IMappingValidationResult;
    persistedValidation: boolean;
}

export interface IListExtractionWarning {
    code: string;
    message: string;
    fieldKey?: string | null;
    detail?: string | null;
}

export interface IListSourceExtractionFieldResult {
    fieldKey: string;
    sourceType: string;
    sourceSheet?: string | null;
    sourceRange?: string | null;
    extractedCount: number;
    extractedValues: IExtractedTemplateValue[];
    sampleValues: IExtractedTemplateValue[];
    warnings: IListExtractionWarning[];
}

export interface IWasteReturnListExtractionResult {
    fields: IListSourceExtractionFieldResult[];
    warnings: IListExtractionWarning[];
}

export interface ISpreadsheetTemplateExtractListValuesResult {
    template: ISpreadsheetTemplateActive;
    mapping: IWasteReturnTemplateMapping;
    extraction: IWasteReturnListExtractionResult;
}

export interface IRegDataWarning {
    code: string;
    message: string;
    fieldKey?: string | null;
    detail?: string | null;
    acknowledgementKey?: string | null;
}

export interface IRegSystemOption {
    id: string;
    fieldKey: string;
    displayValue: string;
    normalizedKey: string;
    code?: string | null;
    description?: string | null;
    structured?: Record<string, unknown>;
    isActive: boolean;
    isDeleted: boolean;
}

export interface IRegTemplateOption {
    rawValue: string;
    normalizedKey: string;
    parsed: Record<string, unknown>;
    sourceSheet?: string | null;
    sourceRange?: string | null;
    sourceRow?: number | null;
}

export interface IRegDataComparisonMatchRow {
    matchStatus: string;
    templateOption: IRegTemplateOption;
    systemRecordId?: string | null;
    systemDisplayValue?: string | null;
    warnings: IRegDataWarning[];
}

export interface IRegDataComparisonFieldCard {
    fieldKey: string;
    label: string;
    extractedCount: number;
    templateOptions: IRegTemplateOption[];
    systemOptions: IRegSystemOption[];
    matches: IRegDataComparisonMatchRow[];
    warnings: IRegDataWarning[];
    usageHints: IRegDataUsageHint[];
}

export interface IRegDataUsageHint {
    systemRecordId: string;
    usageKind: string;
    count: number;
    message: string;
}

export interface IRegDataComparisonResult {
    templateId: string;
    fields: IRegDataComparisonFieldCard[];
    warnings: IRegDataWarning[];
}

export interface IRegDataStagedChange {
    fieldKey: string;
    changeType: string;
    systemRecordId?: string | null;
    templateRawValue?: string | null;
    editedValue?: Record<string, unknown> | null;
}

export interface IRegDataPreviewRequest {
    stagedChanges: IRegDataStagedChange[];
    acknowledgedWarnings: string[];
}

export interface IRegDataValidationMessage {
    code: string;
    message: string;
    fieldKey?: string | null;
    changeType?: string | null;
    systemRecordId?: string | null;
    acknowledgementKey?: string | null;
    isBlocking: boolean;
}

export interface IRegDataUsageWarning {
    fieldKey: string;
    systemRecordId: string;
    usageKind: string;
    count: number;
    message: string;
}

export interface IRegDataPreviewResult {
    templateId: string;
    blockingErrors: IRegDataValidationMessage[];
    warnings: IRegDataValidationMessage[];
    usageWarnings: IRegDataUsageWarning[];
    requiresAcknowledgement: string[];
    canApply: boolean;
}

export interface IRegDataApplyChangeOutcome {
    fieldKey: string;
    changeType: string;
    systemRecordId?: string | null;
    outcome: string;
    recordId?: string | null;
    message?: string | null;
}

export interface IRegDataApplyResult {
    templateId: string;
    appliedAtUtc: string;
    appliedByIdentityId: string;
    totalChangesApplied: number;
    countsByChangeType: Record<string, number>;
    syncAuditId: string;
    changeOutcomes: IRegDataApplyChangeOutcome[];
    warnings: IRegDataValidationMessage[];
}
